import { ReactNode, useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { waitlistService } from '../services/waitlistService'
import CountdownPage from '../pages/CountdownPage'

interface LaunchGateProps {
  children: ReactNode
}

const LaunchGate = ({ children }: LaunchGateProps) => {
  const { isAdmin } = useAuth()
  const [launchDate, setLaunchDate] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchLaunchDate = async () => {
      try {
        const settings = await waitlistService.getLaunchSettings()
        setLaunchDate(settings?.launch_date || null)
      } catch (error) {
        console.error('Error fetching launch settings:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchLaunchDate()
  }, [])

  // Check for admin bypass parameter in URL
  const urlParams = new URLSearchParams(window.location.search)
  const adminBypass = urlParams.get('admin') === 'true'

  // Show loading state while checking
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  // Check if launch date has passed
  const hasLaunched = launchDate ? new Date(launchDate).getTime() <= Date.now() : false

  // Allow access if:
  // 1. Launch date has passed, OR
  // 2. User is admin AND (has bypass parameter OR accessing admin/dashboard routes)
  const shouldBypassCountdown = 
    hasLaunched || 
    (isAdmin() && (
      adminBypass ||
      window.location.pathname.startsWith('/admin') ||
      window.location.pathname.startsWith('/dashboard') ||
      window.location.pathname.startsWith('/calendar') ||
      window.location.pathname.startsWith('/clients') ||
      window.location.pathname.startsWith('/invoices') ||
      window.location.pathname.startsWith('/tasks') ||
      window.location.pathname.startsWith('/profile')
    ))

  if (shouldBypassCountdown) {
    return <>{children}</>
  }

  // Otherwise, show the countdown page
  return <CountdownPage />
}

export default LaunchGate 